<template>
  <div
    v-if="project && template"
    class="board-template"
  >
    <div class="board-template__header">
      <div
        v-if="divisionLogo"
        class="board-template__division-logo"
        :style="`backgroundImage: url('${divisionLogo}')`"
      />

      <input
        v-model.trim="boardLabel"
        class="board-template__label"
        placeholder=" "
        @keyup="changeBoardLabel"
      >

      <input
        v-model.trim="boardName"
        class="board-template__title"
        @keyup="changeBoardName"
        @blur="resetBoardNameIfNeeded"
      >
    </div>
    <div :class="['template__container', `template__container--${templateId}`]">
      <div
        v-for="style in template.items"
        :key="style.index"
        :class="[
          'board-template__template',
          'template',
          `template--${style.index}`
        ]"
        :draggable="isDraggingAllowed"
        @dragover.prevent
        @dragstart="onDragStart($event, style.index)"
        @drop="onDrop($event, style.index)"
        @dragend="onDragEnd($event)"
      >
        <div
          class="board-template__style-preview-container"
          @click="showStyleTemplateModal(style.index)"
        >
          <span
            v-if="styleIdsByIndex[style.index]"
            class="board-template__style-delete"
            @click="showConfirmModal(styleIdsByIndex[style.index], $event)"
          >
            <Button icon>
              <Icon
                icon="cross"
                size="20"
                fill="red"
              />
            </Button>
          </span>
          <StylePreview
            v-if="styleIdsByIndex[style.index]"
            rounded
            :style-id="styleIdsByIndex[style.index]"
            :image-resolution="imageResolution"
          />
        </div>
      </div>
    </div>
    <Confirm
      v-if="showConfirmDeleteModal"
      @close="closeConfirmModal"
      @confirm="confirmDeleteStyle"
    >
      <div slot="header">
        Are you 100% Sure?
      </div>
      <div slot="body">
        <span class="board-template__style-name">
          {{ styleToDelete.styleName }}
        </span>
        will be gone for good.
      </div>
    </Confirm>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import debounce from 'debounce'

import { DragAndDrop } from '@/mixins/DragAndDrop'
import { Projects } from '@/mixins/projects'
import { S3Trim } from '@/mixins/s3Trim'

import { templates } from '@/resources/templates/board.json'

import { imageResolutions } from '@/constants/image'

import Confirm from '@/components/dialogs/Confirm.vue'
import Button from '@/components/Button.vue'
import Icon from '@/components/Icon.vue'
import StylePreview from '@/components/project/StylePreview.vue'

export default {
  name: 'BoardTemplate',

  components: {
    Button,
    Icon,
    Confirm,
    StylePreview
  },

  mixins: [Projects, DragAndDrop, S3Trim],

  props: {
    templateId: {
      required: true,
      type: String,
      default: '0'
    },
    boardId: {
      required: true,
      type: Number
    }
  },

  data: () => ({
    projectId: null,
    showConfirmDeleteModal: false,
    styleToDelete: {},
    template: null,
    styleNameDefault: 'New Style',
    boardName: null,
    boardLabel: null,
    isShowingStyleTemplateModal: false,
    isDraggingAllowed: true
  }),

  computed: {
    ...mapGetters([
      'getBoardById',
      'getStylesByBoardId',
      'getBoardPreviewOption',
      'getStyleById',
      'getError'
    ]),

    /**
     * @returns {string}
     */
    imageResolution () {
      if (this.template.id <= 1) {
        return imageResolutions.STYLE_SINGLE
      } else {
        return imageResolutions.STYLE_SHARED
      }
    },

    /**
     * @returns {object}
     */
    board () {
      return this.getBoardById(this.boardId)
    },

    /**
     * @returns {object}
     */
    styles () {
      return this.getStylesByBoardId(this.boardId)
    },

    /**
     * @returns {Array}
     */
    styleIdsByIndex () {
      const styleIdsByIndex = {}

      this.styles.forEach(style => {
        styleIdsByIndex[style.index] = style.id
      })

      return styleIdsByIndex
    },

    /**
     * @returns {string | null}
     */
    divisionLogo () {
      return this.project
        ? this.s3Trim(this.project.group.assets_logo, null, false)
        : null
    },

    /**
     * @returns {object}
     */
    error () {
      return this.getError()
    }
  },

  watch: {
    /**
     */
    templateId () {
      this.fetchTemplate(this.templateId)
    },

    /**
     */
    board () {
      this.initializeBoard()
    },

    /**
     * @param {object} value
     */
    error (value) {
      if (value) {
        this.$alert(value.message, 'Whoops!', {
          confirmButtonText: 'Got it',
          showClose: false,
          center: true,
          callback: () => {
            this.setError(null)
          }
        })
      }
    }
  },

  created () {
    this.fetchTemplate(this.templateId)

    this.initializeBoard()
  },

  methods: {
    ...mapActions([
      'fetchProjects',
      'setLatestDeliveryAssigned',
      'createStyle',
      'deleteStyle',
      'updateBoardLabel',
      'updateBoardName',
      'updateBoardPreviewOptionStoreOnly',
      'setError'
    ]),

    /**
     */
    initializeBoard () {
      this.boardName = this.board.name
      this.boardLabel = this.board.label
    },

    /**
     * @param {Event}  event
     * @param {number} styleIndex
     */
    onDragStart (event, styleIndex) {
      if (this.getDraggingItem()) {
        event.preventDefault()
        event.stopPropagation()

        return
      }

      this.isDraggingAllowed = false
      event.stopPropagation()

      event.target.classList.add('dragged')

      const style = this.styles.filter(style => style.index === styleIndex)[0]
      this.startDragging(style)
    },

    /**
     * @param {Event}  event
     * @param {number} styleIndex
     */
    async onDrop (event, styleIndex) {
      event.target.classList.remove('dragged')
      event.stopPropagation()

      const styleOrigin = this.getDraggingItem()

      if (!styleOrigin) {
        return
      }

      let styleDestination = this.styles.find(
        style => style.index === styleIndex
      )

      if (!styleDestination) {
        styleDestination = { index: styleIndex }
      }

      const boardIdOrigin = styleOrigin.board_id

      await this.stopDraggingSwap({
        type: 'style',
        destination: styleDestination,
        boardId: this.boardId
      })

      this.updateBoardPreview(boardIdOrigin, this.boardId)
      this.isDraggingAllowed = true
    },

    /**
     * @param {object} boardOriginId
     * @param {object} boardDestinationId
     */
    updateBoardPreview (boardOriginId, boardDestinationId) {
      this.updateBoardPreviewOptionStoreOnly({
        boardId: boardOriginId,
        previewOption: this.getBoardPreviewOption(boardOriginId)
      })

      if (boardOriginId !== boardDestinationId) {
        this.updateBoardPreviewOptionStoreOnly({
          boardId: boardDestinationId,
          previewOption: this.getBoardPreviewOption(boardDestinationId)
        })
      }
    },

    /**
     * @param {Event} event
     */
    onDragEnd (event) {
      event.target.classList.remove('dragged')
    },

    /**
     * @param   {string}        itemIndex
     *
     * @returns {object | null}
     */
    getStyleByIndex (itemIndex) {
      return this.styles.find(anItem => {
        return parseInt(anItem.index) === parseInt(itemIndex)
      })
    },

    /**
     * @param {number} itemIndex
     */
    async showStyleTemplateModal (itemIndex) {
      if (this.isShowingStyleTemplateModal === true) {
        return
      }

      this.isShowingStyleTemplateModal = true
      let style = this.getStyleByIndex(itemIndex)

      if (!style) {
        style = await this.createNewStyle(itemIndex)
      }

      this.$router.push({ path: `create/${style.id}` })
    },

    /**
     * @param   {number} itemIndex
     *
     * @returns {object}
     */
    async createNewStyle (itemIndex) {
      const payload = {
        projectId: this.projectId,
        boardId: this.boardId,
        layout: '0', // We only have one style template at the moment
        name: this.styleNameDefault,
        index: itemIndex
      }

      return await this.createStyle(payload)
    },

    /**
     * @param {string} templateId
     */
    fetchTemplate (templateId) {
      this.template = templates.find(template => template.id === templateId)
    },

    /**
     * @param {number} styleToDeleteId
     * @param {Event}  clickEvent
     */
    showConfirmModal (styleToDeleteId, clickEvent) {
      clickEvent.stopPropagation()
      const style = this.getStyleById(styleToDeleteId)

      this.styleToDelete = {
        styleId: style.id,
        styleName: style.name
      }

      this.showConfirmDeleteModal = true
    },

    /**
     */
    closeConfirmModal () {
      this.showConfirmDeleteModal = false
      this.styleToDelete = {}
    },

    /**
     */
    async confirmDeleteStyle () {
      const payload = {
        styleId: this.styleToDelete.styleId
      }

      await this.deleteStyle(payload)

      const boardPreviewOption = this.getBoardPreviewOption(this.boardId)
      this.updateBoardPreviewOptionStoreOnly({
        boardId: this.boardId,
        previewOption: boardPreviewOption
      })

      this.closeConfirmModal()
    },

    /**
     * @param {Event} event
     */
    changeBoardName (event) {
      const boardNameOld = this.board.name

      if (this.isEnterKey(event)) {
        event.currentTarget.blur()
      } else if (this.boardName !== boardNameOld && this.boardName !== '') {
        this.saveBoardName()
      }
    },

    saveBoardName: debounce(
      /**
       *
       */
      function () {
        const payload = {
          projectId: this.projectId,
          boardId: this.boardId,
          boardName: this.boardName
        }

        this.updateBoardName(payload)
      }, 300),

    /**
     * @param   {Event}   event
     *
     * @returns {boolean}
     */
    isEnterKey (event) {
      return event.key === 'Enter'
    },

    /**
     * @param {Event} event
     */
    resetBoardNameIfNeeded (event) {
      const boardNameOld = this.board.name

      if (this.boardName === '') {
        this.boardName = boardNameOld
      }
    },

    /**
     * @param {Event} event
     */
    changeBoardLabel (event) {
      const boardLabelOld = this.board.label
      let boardLabel = this.boardLabel

      if (!boardLabel || boardLabel.length === 0) {
        boardLabel = null
      }

      if (this.isEnterKey(event)) {
        event.currentTarget.blur()
      } else if (boardLabel !== boardLabelOld) {
        this.saveBoardLabel(boardLabel)
      }
    },

    saveBoardLabel: debounce(
      /**
       * @param {string} boardLabel
       */
      function (boardLabel) {
        const payload = {
          projectId: this.projectId,
          boardId: this.boardId,
          boardLabel
        }

        this.updateBoardLabel(payload)
      }, 300)
  }
}
</script>

<style lang="scss" scoped>
@import '../../styles/board-template';

$z-index-button-delete-style: 99;
$small-spacer: $spacer-unit / 4;

.board-template {
  @include size(100%);

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .board-template__header {
    width: 100%;
  }
}

.dragged {
  button {
    display: none;
  }
}

.board-template__division-logo {
  height: $spacer-unit-x2;
  margin-bottom: $spacer-unit;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: contain;
}

.board-template__title {
  @include text-ellipsis;

  width: 100%;
  padding-bottom: $spacer-unit;
  color: $dark-blue;
  font-weight: $font-weight-medium;
  text-align: center;
  text-transform: uppercase;
}

.board-template__label {
  color: $black;
  border: $border-board-label;
}

.board-template__label,
.board-template__label:placeholder-shown {
  @include text-ellipsis;

  position: absolute;
  top: 0;
  right: 0;
  width: $width-board-label;
  padding: spacing(0.5);
  font-weight: $font-weight-medium;
  font-size: $font-size-medium;
  text-align: center;
  text-transform: uppercase;
  border-radius: $border-radius-default;
}

.board-template__label:placeholder-shown {
  background-color: $blue-light;
  border: $small-spacer solid $blue-light;
}

.board-template__style-delete {
  position: absolute;
  top: $small-spacer;
  right: $small-spacer;
  z-index: $z-index-button-delete-style;
  opacity: 0;

  button {
    border-radius: $border-radius-round;
  }
}

.board-template__template {
  @include size(100%);

  position: relative;
  display: flex;
  background-color: $blue-light;
  border: $border-grey;
  border-radius: $border-radius-default;

  &:hover {
    .board-template__style-delete {
      opacity: 1;
    }
  }
}

[draggable='true'].board-template__template {
  cursor: pointer;
}

.board-template__style-preview-container {
  width: 100%;
}

.board-template__style-name {
  color: $grey-blue;
  text-transform: uppercase;
}
</style>
